import * as React from 'react';
import {
    DefaultButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
} from '@fluentui/react';
import type {
    AgentTaskQuestion,
    AgentTaskQuestionView,
} from '../types';
import {
    buildAgentTaskQuestionsPageUrl,
    getAgentTaskQuestions,
    resolveAgentTaskQuestions,
} from '../questionsApi';
import {
    AgentTaskQuestionCollectionActions,
    AgentTaskQuestionEditor,
    useAgentTaskQuestionCollectionEditor,
} from '../questions/AgentTaskQuestionComponents';

const QUESTION_PAGE_SIZE = 100;

export interface TaskClarificationsTabProps {
    serviceName: string;
    taskListId: number;
    taskId: number;
    onUpdated?: () => void;
}

async function getAllTaskQuestions(
    serviceName: string,
    taskListId: number,
    taskId: number,
    view: AgentTaskQuestionView,
) {
    const questions: AgentTaskQuestion[] = [];
    let pageNumber = 0;

    while (true) {
        const result = await getAgentTaskQuestions(serviceName, {
            view,
            pageNumber,
            pageSize: QUESTION_PAGE_SIZE,
            taskListId,
            taskId,
        });
        questions.push(...result.questions);

        const effectivePageSize = Math.max(1, result.pageSize || QUESTION_PAGE_SIZE);
        const pageCount = Math.max(1, Math.ceil(result.totalCount / effectivePageSize));
        if (result.questions.length === 0 || pageNumber + 1 >= pageCount) {
            break;
        }

        pageNumber += 1;
    }

    return questions;
}

function HistoryProvenance({ question }: { question: AgentTaskQuestion }) {
    if (!question.resolvedBy) {
        return null;
    }

    return (
        <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
            Resolution provenance: {question.resolvedBy}
        </Text>
    );
}

export function TaskClarificationsTab({
    serviceName,
    taskListId,
    taskId,
    onUpdated,
}: TaskClarificationsTabProps) {
    const [openQuestions, setOpenQuestions] = React.useState<AgentTaskQuestion[]>([]);
    const [historyQuestions, setHistoryQuestions] = React.useState<AgentTaskQuestion[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const requestId = React.useRef(0);
    const editor = useAgentTaskQuestionCollectionEditor(openQuestions);

    const loadQuestions = React.useCallback(async () => {
        const currentRequestId = ++requestId.current;
        setLoading(true);
        setError(null);

        try {
            const [active, history] = await Promise.all([
                getAllTaskQuestions(serviceName, taskListId, taskId, 'active'),
                getAllTaskQuestions(serviceName, taskListId, taskId, 'history'),
            ]);
            if (requestId.current !== currentRequestId) return;

            setOpenQuestions(active.filter(question => !question.isResolved));
            setHistoryQuestions(history.filter(question => question.isResolved));
            editor.resetDrafts();
        } catch (err) {
            if (requestId.current !== currentRequestId) return;

            setOpenQuestions([]);
            setHistoryQuestions([]);
            setError(err instanceof Error ? err.message : 'Failed to load task clarifications.');
        } finally {
            if (requestId.current === currentRequestId) {
                setLoading(false);
            }
        }
    }, [editor.resetDrafts, serviceName, taskId, taskListId]);

    React.useEffect(() => {
        void loadQuestions();
        return () => {
            requestId.current += 1;
        };
    }, [loadQuestions]);

    async function submitChangedAnswers() {
        const resolutions = editor.getChangedResolutions();
        if (resolutions.length === 0) return;
        if (resolutions.some(resolution => !resolution.declined && !resolution.answer)) {
            setError('Enter an answer or decline each changed question.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await resolveAgentTaskQuestions(serviceName, resolutions);
            editor.resetDrafts();
            await loadQuestions();
            onUpdated?.();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to resolve task clarifications.');
        } finally {
            setSaving(false);
        }
    }

    const questionsPageUrl = buildAgentTaskQuestionsPageUrl(serviceName, { taskListId, taskId });

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap tokens={{ childrenGap: 8 }}>
                <Text variant="large">Clarifications</Text>
                <Stack horizontal verticalAlign="center" wrap tokens={{ childrenGap: 8 }}>
                    <a href={questionsPageUrl}>Open full questions page</a>
                    <DefaultButton
                        text="Refresh"
                        iconProps={{ iconName: 'Refresh' }}
                        disabled={loading || saving}
                        onClick={() => void loadQuestions()}
                    />
                </Stack>
            </Stack>

            {error && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    actions={(
                        <DefaultButton
                            text="Retry"
                            disabled={loading || saving}
                            onClick={() => void loadQuestions()}
                        />
                    )}
                >
                    {error}
                </MessageBar>
            )}

            {loading ? (
                <Spinner size={SpinnerSize.large} label="Loading task clarifications..." />
            ) : (
                <>
                    <section aria-labelledby="task-open-clarifications-heading">
                        <Text id="task-open-clarifications-heading" variant="mediumPlus">
                            Open questions
                        </Text>
                        {openQuestions.length === 0 ? (
                            <MessageBar messageBarType={MessageBarType.info}>
                                There are no open clarification questions for this task.
                            </MessageBar>
                        ) : (
                            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 8 } }}>
                                {openQuestions.map(question => (
                                    <AgentTaskQuestionEditor
                                        key={question.questionId}
                                        question={question}
                                        draft={editor.getDraft(question)}
                                        readOnly={false}
                                        onChange={patch => editor.updateDraft(question, patch)}
                                    />
                                ))}
                                <AgentTaskQuestionCollectionActions
                                    editor={editor}
                                    saving={saving}
                                    onSubmit={() => void submitChangedAnswers()}
                                />
                            </Stack>
                        )}
                    </section>

                    <section aria-labelledby="task-clarification-history-heading">
                        <Text id="task-clarification-history-heading" variant="mediumPlus">
                            Resolved and declined
                        </Text>
                        {historyQuestions.length === 0 ? (
                            <MessageBar messageBarType={MessageBarType.info}>
                                No resolved or declined clarification questions were found for this task.
                            </MessageBar>
                        ) : (
                            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 8 } }}>
                                {historyQuestions.map(question => (
                                    <div key={question.questionId}>
                                        <AgentTaskQuestionEditor
                                            question={question}
                                            draft={{
                                                answer: question.answer ?? '',
                                                declined: question.declined,
                                            }}
                                            readOnly={true}
                                        />
                                        <HistoryProvenance question={question} />
                                    </div>
                                ))}
                            </Stack>
                        )}
                    </section>
                </>
            )}
        </Stack>
    );
}
